import React, { useEffect, useMemo } from "react";
import { MainLayout } from "../layout/mainLayout";
import { PageTitle } from "../layout/pageTitle";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, List, ListItem, ListItemButton, ListItemText, Typography } from "@mui/material";
import { useSelectCurrentWorkShiftTimeSlot } from "../../hooks/worktime/useSelectWorkShiftTimeSlots";
import { useAppSelector } from "../../framework/redux/hooks";
import { Base } from "../../framework/common/base";
import { getServiceUrgency, isServiceVisibleForDriver } from "./vehicleMaintenanceUtils";
import type { VehicleMaintenanceDto } from "../../models/vehicleMaintenance/vehicleMaintenance";
import { MaintenanceReminderStatus } from "../../models/vehicleMaintenance/vehicleMaintenanceReminder";

type VehicleMaintenanceSliceShape = {
    vehicleId: string | null;
    items: VehicleMaintenanceDto[];
};

export const VehicleMaintenanceList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const current = useSelectCurrentWorkShiftTimeSlot();

    const state = useAppSelector((s) => s.vehicleMaintenance as VehicleMaintenanceSliceShape);

    const services: VehicleMaintenanceDto[] = useMemo(() => {
        if (!current?.vehicleId) {
            return [];
        }

        if (state.vehicleId !== current.vehicleId) {
            return [];
        }

        return (state.items ?? [])
            .filter((m) => m.vehicleId === current.vehicleId)
            .filter(isServiceVisibleForDriver)
            .sort((a, b) => {
                const aDate = a.reminder?.reminderDate ?? a.maintenanceDate ?? "";
                const bDate = a.reminder?.reminderDate ?? b.maintenanceDate ?? "";
                return Base.dateCompare(aDate, bDate);
            });
    }, [current?.vehicleId, state.items, state.vehicleId]);

    useEffect(() => {
        if (services.length === 1) {
            navigate(`/services/${services[0].id}`, { replace: true });
        }
    }, [services.length]);

    return (
        <MainLayout topComponent={<PageTitle header={t("maintenance.title")} />} hasPx>
            <Box>
                {services.length === 0 ? (
                    <Typography>{t("maintenance.noUpcoming")}</Typography>
                ) : (
                    <List>
                        {services.map((m) => {
                            const urgency = getServiceUrgency(m);
                            const when = m.reminder?.reminderDate ?? m.maintenanceDate;
                            const awaitingApproval = m.reminder?.status === MaintenanceReminderStatus.AwaitingApproval;
                            const whenText = when ? Base.dateToDateTimeStr(when) : t("maintenance.noDate");
                            const secondaryText = awaitingApproval
                                ? `${t("maintenance.awaitingApproval")} \u2022 ${whenText}`
                                : whenText;

                            return (
                                <ListItem key={m.id} disablePadding>
                                    <ListItemButton onClick={() => navigate(`/services/${m.id}`)}>
                                        <ListItemText
                                            primary={t(`maintenance.urgency.${urgency}`)}
                                            secondary={secondaryText}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </Box>
        </MainLayout>
    );
};
